<script>
import { mapState } from 'vuex';
import { Line } from 'vue-chartjs';
import { getFormattedCurrentDate } from '@/utils.js';

export default {
  extends: Line,
  props: {
    datasets: {
      type: Array,
      default: () => []
    },
    isDailyData: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      PREFERRED_STEPS_MS: [
        5 * 60 * 1000, // 5 minuti
        15 * 60 * 1000, // 15 minuti
        30 * 60 * 1000, // 30 minuti
        60 * 60 * 1000, // 1 ora
        3 * 60 * 60 * 1000, // 3 ore
        6 * 60 * 60 * 1000, // 6 ore
        12 * 60 * 60 * 1000, // 12 ore
        24 * 60 * 60 * 1000 // 24 ore
      ],
      chartdata: {},
      mouseEntered: false,
      arrowCanvas: null,
      arrowImage: {},
      options: {
        responsive: true,
        maintainAspectRatio: false,
        elements: {
          point: {
            hoverRadius: 5,
            hoverBorderWidth: 3
          }
        },
        tooltips: {
          enabled: true,
          mode: 'x',
          intersect: false,
          // filter: (tooltipItem, data) => {
          //   return !data.datasets[tooltipItem.datasetIndex].layerName?.includes('DIR');
          // },
          callbacks: {
            title: (tooltipItems) => {
              if (!tooltipItems.length) {
                return '';
              }

              return this.getFormattedDate(tooltipItems[0].xLabel);
            }
          }
        },
        hover: {
          mode: 'x',
          intersect: false,
          animationDuration: 0
        },
        legend: {
          display: false
        },
        scales: {
          yAxes: [
            {
              id: 'y-axis-1',
              display: true,
              type: 'linear',
              position: 'left',
              ticks: {
                display: true
              },
              scaleLabel: {
                display: true,
                labelString: this.datasets[0]?.label || ''
              }
            },
            {
              id: 'y-axis-2',
              display: this.datasets.length > 1,
              type: 'linear',
              position: 'right',
              gridLines: {
                drawTicks: false,
                display: false
              },
              ticks: {
                display: true
              },
              scaleLabel: {
                display: true,
                labelString: this.datasets.length > 1 ? this.datasets[1].label : ''
              }
            }
          ],
          xAxes: [
            {
              id: 'x-axis-1',
              type: 'linear',
              position: 'bottom',
              ticks: {
                autoSkip: false,
                display: true
              },
              gridLines: {
                drawTicks: true,
                display: true
              }
            }
          ]
        }
      }
    };
  },
  computed: {
    ...mapState(['table_chart'])
  },
  mounted() {
    const yAxisArray = [];
    const udmArray = [];
    const combinedLabels = [];
    const axisDatasets = this.datasets.filter((dataset) => !this.isDirectionDataset(dataset)); // eventually remove this condition if you want direction datasets to also be assigned to y-axes

    // if (this.datasets.length > 1) {
    if (axisDatasets.length > 1) {
      Array.from(new Set(axisDatasets.map((dataset) => {
        const match = dataset.label.match(/\(([^)]+)\)/);

        return match ? match[1] : dataset.label;
      }).filter((label) => label !== null))).slice(0, 2).forEach(udm => udmArray.push(udm));

      udmArray.forEach((udm) => {
        // const matchingLabels = this.datasets
        const matchingLabels = axisDatasets
          .filter((dataset) => dataset.label === udm || dataset.label.includes(`(${udm})`))
          .map((dataset) => dataset.label);
        combinedLabels.push(matchingLabels.join(' / '));
      });

      this.datasets.forEach((data) => {
        if (this.isDirectionDataset(data)) {
          yAxisArray.push('direction-axis');

          return;
        } // eventually remove this condition if you want direction datasets to also be assigned to y-axes

        const index = udmArray.findIndex(elem => data.label === elem || data.label.includes(`(${elem})`));
        yAxisArray.push(`y-axis-${index + 1}`);
      });

      this.options.scales.yAxes = udmArray.map((udm, i) => ({
        display: true,
        id: `y-axis-${i + 1}`,
        position: i == 0 ? 'left' : 'right',
        scaleLabel: { display: true, labelString: combinedLabels[i] },
        gridLines: { display: i == 0, drawTicks: i == 0 }
      }));
    } else {
      // this.datasets.forEach((e, i) => yAxisArray.push(`y-axis-${i + 1}`));
      this.datasets.forEach((data) => {
        yAxisArray.push(this.isDirectionDataset(data) ? 'direction-axis' : 'y-axis-1');
      });
      this.options.scales.yAxes = [{
        id: 'y-axis-1',
        display: axisDatasets.length > 0,
        type: 'linear',
        position: 'left',
        gridLines: {
          drawTicks: true,
          display: true
        },
        ticks: {
          display: axisDatasets.length > 0
        },
        scaleLabel: {
          display: axisDatasets.length > 0,
          labelString: axisDatasets[0] ? axisDatasets[0].label : ''
        }
      }]; // eventually remove this condition if you want direction datasets to also be assigned to y-axes
    }

    if (this.datasets.some(dataset => this.isDirectionDataset(dataset))) {
      this.options.scales.yAxes.push({
        id: 'direction-axis',
        display: false,
        type: 'linear',
        ticks: {
          min: 0,
          max: 360
        }
      });
    } // eventually remove this condition if you want direction datasets to also be assigned to y-axes

    const labelTimestamps = this.table_chart.rasterVectorChartLabels.map(l => new Date(l).getTime());

    // Calcolo timestep reale
    const timeStepMsVector = [];
    this.table_chart.rasterVectorChartDataSetLine.forEach((dataset) => {
      let lastDataTimeStamp = null;
      let timeStepMs = Infinity;

      for (let i = 0;i < labelTimestamps.length;i++) {
        if (dataset.data[i] !== null) {
          if (lastDataTimeStamp) {
            const diff = Math.abs(labelTimestamps[i] - lastDataTimeStamp);

            if (diff > 0 && diff < timeStepMs) {
              timeStepMs = diff;
            }
          }

          lastDataTimeStamp = labelTimestamps[i];
        }
      }

      timeStepMsVector.push(timeStepMs);
    });

    // Creazione array label Timestamp completo basato sul passo minimo reale
    const minTimeStepMs = Math.min(...timeStepMsVector.filter(step => Number.isFinite(step) && step > 0));
    const completeLabelTimestamps = [];

    if (labelTimestamps.length && Number.isFinite(minTimeStepMs)) {
      const sortedTimestamps = [...labelTimestamps].sort((a, b) => a - b);
      const startTs = sortedTimestamps[0];
      const endTs = sortedTimestamps[sortedTimestamps.length - 1];

      for (let ts = startTs;ts <= endTs;ts += minTimeStepMs) {
        completeLabelTimestamps.push(ts);
      }
    } else {
      completeLabelTimestamps.push(...labelTimestamps);
    }

    // Formattazione dati in oggetti {x, y}
    const formattedDatasets = this.table_chart.rasterVectorChartDataSetLine.map((dataset, index) => {
      const xyData = [];
      let lastTs = null;
      const gapThreshold = timeStepMsVector[index] * 1.5; // Definiamo un buco "reale" se manca più di 1.5 volte il tempo previsto

      completeLabelTimestamps.forEach((timestep) => {
        const dataIndex = labelTimestamps.indexOf(timestep);
        const val = dataIndex !== -1 ? dataset.data[dataIndex] : null;

        if (val !== null && val !== undefined) {
          // Se c'è un buco temporale reale, inseriamo null per tutti gli step mancanti
          if (lastTs !== null && (timestep - lastTs) > gapThreshold) {
            let gapTs = lastTs + timeStepMsVector[index];

            // Riempi tutti gli step intermedi con punti null
            while (gapTs < timestep) {
              xyData.push({ x: gapTs, y: null });
              gapTs += timeStepMsVector[index];
            }
          }

          xyData.push({ x: timestep, y: val });
          lastTs = timestep;
        }
      });

      return {
        ...dataset,
        data: xyData,
        yAxisID: yAxisArray[index],
        spanGaps: false
      };
    });

    this._preferredXAxisStep = null;
    this._xAxisStartValue = Math.min(...completeLabelTimestamps);

    // Configurazione Asse X per oggetti {x, y}
    this.options.scales.xAxes[0].afterBuildTicks = (axis) => {
      const preferredStep = this.getPreferredXAxisStep(axis, minTimeStepMs);
      const startValue = Math.min(...completeLabelTimestamps);

      this._preferredXAxisStep = preferredStep;
      this._xAxisStartValue = axis.min != null ? axis.min : this._xAxisStartValue;

      axis.ticks = completeLabelTimestamps.filter((timestamp) => {
        return ((timestamp - startValue) % preferredStep) === 0;
      });

      if (!axis.ticks.length) {
        axis.ticks = completeLabelTimestamps;
      }
    };

    // Forza i limiti del grafico per eliminare gli spazi vuoti
    this.options.scales.xAxes[0].ticks.min = Math.min(...labelTimestamps);
    this.options.scales.xAxes[0].ticks.max = Math.max(...labelTimestamps);

    this.options.scales.xAxes[0].ticks.userCallback = (value) => {
      return this.getFormattedDate(value); // Formattazione completa con data e ora
    };

    this.chartdata = {
      datasets: formattedDatasets
    };

    if (this.mobileBreakpoints()) {
      this.options.tooltips.enabled = false;
      this.options.tooltips.custom = this.externalTooltipHandler;
    }

    this.$emit('chart_data', this.chartdata);
    this.separateDaysPlugin();
    this.hideDirectionDatasetsPlugin();
    this.hoverLinePlugin();
    this.drawArrowsPlugin();
    this.renderChart(this.chartdata, this.options);
    this.emitLegendData();
    this.setupArrowOverlay();
    this.addEventListeners();
  },
  methods: {
    isLandscape() {
      return /Mobi|Android/i.test(navigator.userAgent) && window.innerHeight < window.innerWidth;
    },
    mobileBreakpoints() {
      return /Mobi|Android/i.test(navigator.userAgent) || this.isLandscape();
    },
    externalTooltipHandler(tooltipModel) {
      if (!tooltipModel || tooltipModel.opacity === 0 || !tooltipModel.body) {
        return;
      }

      const items = tooltipModel.body.map(({ lines }, i) => ({
        text: lines.join(' '),
        color: this.getDatasetColor(this.chartdata.datasets[tooltipModel.dataPoints?.[i]?.datasetIndex])
      })).filter(item => item.text);

      this.$emit('tooltip_data', {
        title: (tooltipModel.title || []).join(' '),
        items
      });
    },
    isDirectionDataset(dataset) {
      return dataset?.udm?.trim() === '°';
    },
    emitLegendData() {
      const chart = this._chart || this.$data._chart;

      if (!chart) {
        return;
      }

      this.$emit('legend_data', chart.data.datasets.map((dataset, index) => ({
        datasetIndex: index,
        label: dataset.label,
        color: this.getDatasetColor(dataset),
        hidden: !chart.isDatasetVisible(index)
      })));
    },
    toggleDataset(index) {
      const chart = this._chart || this.$data._chart;

      if (!chart) {
        return;
      }

      const meta = chart.getDatasetMeta(index);
      meta.hidden = chart.isDatasetVisible(index) ? true : null;
      chart.update();
      this.emitLegendData();
    },
    hoverLinePlugin() {
      this.addPlugin({
        afterDatasetsDraw: chart => {
          const activeElements = chart.tooltip?._active;

          if (!activeElements || !activeElements.length) {
            return;
          }

          const x = activeElements[0].tooltipPosition().x;
          const chartArea = chart.chartArea;
          const ctx = chart.ctx;

          ctx.save();
          ctx.beginPath();
          ctx.moveTo(x, chartArea.top);
          ctx.lineTo(x, chartArea.bottom);
          ctx.lineWidth = 1;
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
          ctx.setLineDash([4, 4]);
          ctx.stroke();
          ctx.restore();
        }
      });
    },
    hideDirectionDatasetsPlugin() {
      this.addPlugin({
        beforeDatasetDraw: (chart, args) => {
          const dataset = chart.data.datasets[args.index];

          if (this.isDirectionDataset(dataset)) {
            return false;
          }
        }
      });
    },
    addEventListeners() {
      this.$refs.canvas.addEventListener('mousemove', () => {
        if (!this.mouseEntered) {
          this.mouseEntered = true;
          this.redrawArrows();
        }
      });
      this.$refs.canvas.addEventListener('mouseout', () => {
        if (this.mouseEntered) {
          this.mouseEntered = false;
          this.redrawArrows();
        }
      });
    },
    setupArrowOverlay() {
      const chart = this._chart || this.$data._chart;
      const canvas = this.$refs.canvas;

      if (!chart || !canvas || this.arrowCanvas) {
        return;
      }

      const parent = canvas.parentElement;

      if (!parent) {
        return;
      }

      parent.style.position = 'relative';

      const arrowCanvas = document.createElement('canvas');
      arrowCanvas.width = canvas.width;
      arrowCanvas.height = canvas.height;
      arrowCanvas.style.position = 'absolute';
      arrowCanvas.style.top = '0';
      arrowCanvas.style.left = '0';
      arrowCanvas.style.width = '100%';
      arrowCanvas.style.height = '100%';
      arrowCanvas.style.pointerEvents = 'none';
      arrowCanvas.style.opacity = '1';
      arrowCanvas.style.transition = 'opacity 0.3s ease';

      parent.appendChild(arrowCanvas);
      this.arrowCanvas = arrowCanvas;
      this.drawArrows(chart);
    },
    setArrowOverlayOpacity(opacity) {
      if (this.arrowCanvas) {
        this.arrowCanvas.style.opacity = opacity;
      }
    },
    redrawArrows() {
      const chart = this._chart || this.$data._chart;

      if (chart) {
        chart.draw();
      }
    },
    getArrowImage(color = '#000000') {
      if (this.arrowImage[color]) {
        return this.arrowImage[color];
      }

      const svgCode = `
        <svg width="13px" height="13px" viewBox="0 0 0.281 0.281" xmlns="http://www.w3.org/2000/svg" id="arrow" version="1.1" xmlns:xlink="http://www.w3.org/1999/xlink" transform="matrix(6.123233995736766e-17,1,-1,6.123233995736766e-17,0,0)">
          <path fill="none" stroke="#ffffff" stroke-width="0.02" stroke-linejoin="round" d="M0.155 0.043a0.019 0.019 0 0 1 0.027 0l0.084 0.084a0.019 0.019 0 0 1 0 0.027l-0.084 0.084a0.019 0.019 0 0 1 -0.027 -0.027L0.206 0.159H0.028a0.019 0.019 0 0 1 0 -0.037H0.206L0.155 0.07a0.019 0.019 0 0 1 0 -0.027Z"/>
          <path fill="${color}" d="M0.155 0.043a0.019 0.019 0 0 1 0.027 0l0.084 0.084a0.019 0.019 0 0 1 0 0.027l-0.084 0.084a0.019 0.019 0 0 1 -0.027 -0.027L0.206 0.159H0.028a0.019 0.019 0 0 1 0 -0.037H0.206L0.155 0.07a0.019 0.019 0 0 1 0 -0.027Z"/>
        </svg>
      `;
      const img = new Image();
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgCode)}`;
      this.arrowImage[color] = img;

      return img;
    },
    drawArrows(chart) {
      const directionDatasetsInfo = this.getDirectionDatasetsInfo(chart);

      if (!directionDatasetsInfo.length) {
        return;
      }

      const xAxis = chart.scales['x-axis-1'];
      const yAxis = chart.scales['y-axis-1'];
      const ctx = chart.ctx;
      const arrowOpacity = 1;
      // const arrowOpacity = this.mouseEntered ? 0.3 : 1;
      const arrowImages = directionDatasetsInfo.map(({ dataset }) => {
        return this.getArrowImage(this.getDatasetColor(dataset));
      });

      const draw = () => {
        directionDatasetsInfo.forEach(({ dataset: directionDataset }, rowIndex) => {
          const arrowImage = arrowImages[rowIndex];

          directionDataset.data.forEach((point) => {
            const value = point && typeof point === 'object' ? point.y : point;

            if (value == null || value === '') {
              return;
            }

            const pointX = point && typeof point === 'object' ? point.x : null;

            if (pointX == null) {
              return;
            }

            const startValue = this._xAxisStartValue != null ? this._xAxisStartValue : pointX;

            if (this._preferredXAxisStep && ((pointX - startValue) % this._preferredXAxisStep) !== 0) {
              return;
            }

            const svgX = xAxis.getPixelForValue(pointX) - 8;
            const svgY = yAxis.top + 20 + rowIndex * 24;
            const rotationAngle = value * Math.PI / 180;

            ctx.save();
            ctx.globalAlpha = arrowOpacity;
            ctx.translate(svgX + 8, svgY + 8);
            ctx.rotate(rotationAngle);
            ctx.drawImage(arrowImage, -8, -8, 16, 16);
            ctx.restore();
          });
        });
      };

      if (arrowImages.every(image => image.complete)) {
        draw();
      } else {
        arrowImages.forEach(image => {
          image.onload = () => {
            chart.draw();
          };
        });
      }
    },
    getDirectionDatasetsInfo(chart) {
      return chart.data.datasets.reduce((directionDatasets, dataset, datasetIndex) => {
        if (this.isDirectionDataset(dataset) && chart.isDatasetVisible(datasetIndex)) {
          directionDatasets.push({
            dataset,
            datasetIndex
          });
        }

        return directionDatasets;
      }, []);
    },
    getDatasetColor(dataset) {
      if (!dataset) {
        return '#000000';
      }

      const color = dataset.borderColor || dataset.backgroundColor;

      return Array.isArray(color) ? color[0] : color || '#000000';
    },
    separateDaysPlugin() {
      this.addPlugin({
        beforeDatasetsDraw: chart => {
          const ctx = chart.ctx;
          const xAxis = chart.scales['x-axis-1'];
          const yAxis = chart.scales['y-axis-1'];
          const chartArea = chart.chartArea;

          if (!xAxis || !chartArea) {
            return;
          }

          const xMin = xAxis.min;
          const xMax = xAxis.max;

          if (xMin == null || xMax == null) {
            return;
          }

          let dayStart = new Date();
          const localDate = new Date(xMin);

          if (this.isDailyData) {
            dayStart = new Date(Date.UTC(localDate.getUTCFullYear(), localDate.getUTCMonth(), localDate.getUTCDate()));
          } else {
            dayStart = new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 0, 0, 0, 0);
          }

          let currentDayStart = dayStart.getTime();

          if (currentDayStart > xMin) {
            currentDayStart -= 24 * 60 * 60 * 1000;
          }

          const colors = [
            'rgba(255, 255, 255, 1)',
            'rgba(230, 246, 236, 1)'
          ];

          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';

          let dayIndex = 0;

          while (currentDayStart < xMax) {
            const nextDayStart = currentDayStart + 24 * 60 * 60 * 1000;

            const startValue = Math.max(currentDayStart, xMin);
            const endValue = Math.min(nextDayStart, xMax);
            const startX = xAxis.getPixelForValue(startValue);
            const endX = xAxis.getPixelForValue(endValue);

            ctx.fillStyle = colors[dayIndex % colors.length];
            ctx.fillRect(startX, yAxis ? yAxis.top : chartArea.top, endX - startX, (yAxis ? yAxis.bottom : chartArea.bottom) - (yAxis ? yAxis.top : chartArea.top));

            currentDayStart = nextDayStart;
            dayIndex += 1;
          }

          ctx.restore();
        }
      });
    },
    getFormattedDate(timestamp) {
      const date = getFormattedCurrentDate(new Date(timestamp));

      return this.isDailyData ? date.split(' ')[0] : date; // Se è daily, mostra solo la data senza l'ora
    },
    getPreferredXAxisStep(axis, minTimeStepMs) {
      const maxTicks = Math.max(2, Math.floor((axis.width || 400) / 20));
      const totalSpan = axis.max - axis.min;
      const candidates = this.PREFERRED_STEPS_MS.filter(step => step >= minTimeStepMs);

      for (let i = 0;i < candidates.length;i += 1) {
        const step = candidates[i];

        if (Math.ceil(totalSpan / step) <= maxTicks) {
          return step;
        }
      }

      return candidates.length ? candidates[candidates.length - 1] : minTimeStepMs;
    },
    drawArrowsPlugin() {
      this.addPlugin({
        afterDatasetsDraw: chart => {
          this.drawArrows(chart);
        }
      });
    }
  }
};
</script>
